import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { displayErrorToast } from "@/utils/toast";

export interface RemixRequest {
  share_token: string;
  workspace_id: number;
  name: string;
  folder_id: number | null;
}

export interface RemixResponse {
  success: boolean;
  prompt_template: {
    id: number;
    prompt_name: string;
    workspace_id: number;
    [key: string]: unknown;
  };
}

export const useRemixPrompt = () => {
  const authContext = useAuth();
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useMutation<RemixResponse, Error, RemixRequest>({
    mutationFn: async ({ share_token, ...body }) => {
      const response = await fetch(`${API_URL}/api/sessions/${share_token}/remix`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authContext?.userToken}`,
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remix prompt");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["folder-entities", workspaceId],
      });
    },
    onError: error => {
      displayErrorToast(error.message || "Failed to remix prompt");
    },
  });
};
